import React, { useState, useEffect } from 'react';
import { Play, Clock, Users, Star, CheckCircle, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CoursesPage = ({ user }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, enrolled, completed
  const [accessLevel, setAccessLevel] = useState(null);

  useEffect(() => {
    fetchAccessLevel();
    fetchCourses();
  }, []);

  const fetchAccessLevel = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/access/level`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessLevel(data);
      }
    } catch (error) {
      console.error('Error fetching access level:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/courses/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const isEnrolled = (courseId) => {
    return user?.enrolled_courses?.includes(courseId);
  };

  const isCompleted = (courseId) => {
    return user?.completed_courses?.includes(courseId);
  };

  const handleClaimFreeCourse = async (courseId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/access/claim-free-course`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ course_id: courseId })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message + `\nFree courses remaining: ${data.free_courses_remaining}`);
        fetchCourses(); // Refresh courses
        fetchAccessLevel(); // Refresh access level
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to claim free course');
      }
    } catch (error) {
      console.error('Error claiming free course:', error);
      alert('Error claiming free course. Please try again.');
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/enrollment/enroll/${courseId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        // Refresh user data
        window.location.reload();
      } else {
        const error = await response.json();
        alert(error.detail || 'Enrollment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error enrolling:', error);
      alert('Error enrolling in course. Please try again.');
    }
  };

  const filteredCourses = courses.filter(course => {
    if (filter === 'enrolled') return isEnrolled(course.id);
    if (filter === 'completed') return isCompleted(course.id);
    return true;
  });

  const getCategoryColor = (category) => {
    const colors = {
      'Leadership': 'bg-purple-100 text-purple-800',
      'Spiritual Formation': 'bg-orange-100 text-orange-800',
      'Worship & Ministry': 'bg-blue-100 text-blue-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-light text-gray-900 mb-4">
            Course Catalog
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Transform your life through apostolic and prophetic mentorship. Choose from our comprehensive curriculum designed to raise leaders for this generation.
          </p>
          
          {/* Filters */}
          <div className="flex space-x-4 mt-8">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Courses
            </button>
            <button
              onClick={() => setFilter('enrolled')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'enrolled'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              My Courses
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {/* Course Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {filter === 'enrolled' && 'You are not enrolled in any courses yet.'}
              {filter === 'completed' && 'You have not completed any courses yet.'}
              {filter === 'all' && 'No courses available at the moment.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course) => {
              const enrolled = isEnrolled(course.id);
              const completed = isCompleted(course.id);

              return (
                <div
                  key={course.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  onClick={() => {
                    if (enrolled) {
                      navigate(`/courses/${course.id}`);
                    }
                  }}
                >
                  {/* Course Image */}
                  <div className="relative h-48 bg-gradient-to-br from-purple-600 to-purple-800 overflow-hidden">
                    {course.thumbnail_url && (
                      <img
                        src={course.thumbnail_url}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                    
                    {/* Status Badge */}
                    {completed && (
                      <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                      </div>
                    )}
                    {enrolled && !completed && (
                      <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        In Progress
                      </div>
                    )}
                    {!enrolled && (
                      <div className="absolute top-4 right-4 bg-white/90 text-gray-800 px-3 py-1 rounded-full text-xs font-medium flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        £{course.price_gbp}/month
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div className="absolute bottom-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(course.category)}`}>
                        {course.level}
                      </span>
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                      {course.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {course.description}
                    </p>

                    {/* Course Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {course.duration_weeks} weeks
                      </div>
                      <div className="flex items-center">
                        <Play className="w-4 h-4 mr-1" />
                        {course.total_lessons} lessons
                      </div>
                      <div className="flex items-center">
                        <Star className="w-4 h-4 mr-1 text-yellow-500" />
                        {course.rating || 5.0}
                      </div>
                    </div>

                    {/* Instructor */}
                    <div className="flex items-center mb-4 pb-4 border-b border-gray-100">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                        <span className="text-purple-600 font-semibold text-xs">OA</span>
                      </div>
                      <span className="text-sm text-gray-700">{course.instructor}</span>
                    </div>

                    {/* Action Button */}
                    {enrolled ? (
                      <button
                        onClick={() => {
                          // Check if course has video lessons
                          if (course.title === 'Spiritual Growth') {
                            navigate(`/course/${course.id}`);
                          } else {
                            navigate(`/courses/${course.id}`);
                          }
                        }}
                        className="w-full bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors"
                      >
                        {completed ? 'Review Course' : 'Continue Learning'}
                      </button>
                    ) : accessLevel?.is_mentorship_member && accessLevel?.courses?.free_courses_available > 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClaimFreeCourse(course.id);
                        }}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transition-colors"
                      >
                        Claim Free Course ({accessLevel.courses.free_courses_available} remaining)
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEnroll(course.id);
                          }}
                          className="w-full bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Purchase for £300
                        </button>
                        {!accessLevel?.is_mentorship_member && (
                          <p className="text-xs text-center text-gray-500">
                            Enroll in mentorship to get 2 free courses
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage;
